import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PatchProjectData } from '../../redux/projectDataSlice';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import styles from './css/index.module.css';

// Features that cannot be disabled (always enabled)
const REQUIRED_FEATURES = ['project_overview', 'dataroom'];

// Feature categories matching the backend structure
const FEATURE_CATEGORIES = {
    strategic_assessment: {
        name: 'Strategic Assessment & Monitoring',
        features: {
            project_overview: 'Project Overview',
            maturity_index: 'Maturity Index',
            security_index: 'Security Index',
            team_analyser: 'Team Analyser',
            security_governance: 'Security Governance',
        },
    },
    due_diligence: {
        name: 'Due Diligence & Knowledge',
        features: {
            dataroom: 'Dataroom',
            dataroom_wizard: 'Dataroom Wizard',
            information_requests: 'Information Requests',
            observations: 'Observations',
            lessons_learnt: 'Lessons Learnt',
            journal: 'Journal',
            the_tent: 'The Tent',
            lens_intelligence: 'Lens Intelligence',
        },
    },
    project_risk_management: {
        name: 'Project & Risk Management',
        features: {
            project_management: 'Project Management',
            raid_log: 'RAID Log',
            raci_matrix: 'RACI Matrix',
            action_log: 'Action Log',
        },
    },
    technical_service: {
        name: 'Technical & Service Delivery',
        features: {
            software_inventory: 'Software Inventory',
            hardware_inventory: 'Hardware Inventory',
            ticket_management: 'Ticket Management',
        },
    },
};

const AddLensFeatures = ({ createdProjectData, setCreatedProjectData, setSelectedView }) => {
    const dispatch = useDispatch();
    const { projectDetailButtonLoading } = useSelector(state => state.projectData);
    const { memberships, activeWorkspaceId } = useSelector(state => state.user);

    // Initialize with static default enabled features (no workspace coupling)
    const [enabledFeatures, setEnabledFeatures] = React.useState(() => {
        const allFeatures = {};
        Object.values(FEATURE_CATEGORIES).forEach(category => {
            Object.keys(category.features).forEach(featureKey => {
                allFeatures[featureKey] = false;
            });
        });

        const activeMembership = memberships.find(membership => membership.workspace.id === activeWorkspaceId);
        console.log({ activeMembership });
        activeMembership?.workspace?.default_lens_features?.forEach(feature => {
            allFeatures[feature] = true;
        });

        // Ensure required features are always enabled
        REQUIRED_FEATURES.forEach(key => {
            allFeatures[key] = true;
        });

        return allFeatures;
    });

    const handleToggleFeature = featureKey => {
        // Prevent disabling required features
        if (REQUIRED_FEATURES.includes(featureKey)) {
            return;
        }
        setEnabledFeatures(prev => ({
            ...prev,
            [featureKey]: !prev[featureKey],
        }));
    };

    const handleAddAll = () => {
        const allFeatures = {};
        Object.values(FEATURE_CATEGORIES).forEach(category => {
            Object.keys(category.features).forEach(featureKey => {
                allFeatures[featureKey] = true;
            });
        });
        // Ensure required features are always enabled
        REQUIRED_FEATURES.forEach(key => {
            allFeatures[key] = true;
        });
        setEnabledFeatures(allFeatures);
    };

    const handleSave = async e => {
        e.preventDefault();
        if (!createdProjectData?.id) return;

        // Ensure required features are always enabled before saving
        const featuresToSave = { ...enabledFeatures };
        REQUIRED_FEATURES.forEach(key => {
            featuresToSave[key] = true;
        });

        await dispatch(
            PatchProjectData({
                id: createdProjectData.id,
                data: { enabled_features: featuresToSave },
            }),
        );

        // Update createdProjectData with enabled_features
        setCreatedProjectData({
            ...createdProjectData,
            enabled_features: featuresToSave,
        });

        // Move to next step
        setSelectedView('projectDetails');
    };
    return (
        <div className={styles.componentCard} style={{ width: '600px', maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto' }}>
            <h5 className={styles.componentTitle}>Add Lens Features</h5>

            <div className={styles.featureCategories}>
                {Object.entries(FEATURE_CATEGORIES).map(([categoryKey, category]) => (
                    <div key={categoryKey} className={styles.featureCategory}>
                        <h4 className={styles.categoryTitle}>{category.name}</h4>
                        <div className={styles.featureList}>
                            {Object.entries(category.features).map(([featureKey, featureName]) => {
                                const isRequired = REQUIRED_FEATURES.includes(featureKey);
                                const isChecked = enabledFeatures[featureKey] === true;
                                return (
                                    <div key={featureKey} className={styles.featureItem}>
                                        <label className={styles.featureCheckbox} style={isRequired ? { opacity: 0.6, cursor: 'not-allowed' } : {}}>
                                            <input type="checkbox" checked={isChecked} disabled={isRequired} onChange={() => handleToggleFeature(featureKey)} />
                                            <span>
                                                {featureName}
                                                {isRequired && <span style={{ marginLeft: '8px', fontSize: '12px', color: '#666' }}>(Required)</span>}
                                            </span>
                                        </label>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            <div className="d-flex align-items-center justify-content-between" style={{ marginTop: '32px' }}>
                <button type="button" onClick={handleSave} disabled={projectDetailButtonLoading || !createdProjectData?.id} className={styles.submitButton}>
                    Save {projectDetailButtonLoading && <AiOutlineLoading3Quarters className="ms-2 rotateLoader" />}
                </button>
                <span role="button" onClick={handleAddAll} className={styles.linkBtn} style={{ color: 'var(--Brand-Secondary)' }}>
                    Add all
                </span>
            </div>
        </div>
    );
};

export default AddLensFeatures;
